import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Navbar, Footer } from "../../components/Layout";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Plus, Trash2, X, Edit, Save } from "lucide-react";

const DEFAULT_ROOMS = [
  { name: "Entrée / Bureau", items: [{ label: "Murs" }, { label: "Sol" }, { label: "Plafond" }, { label: "Éclairage" }] },
  { name: "Atelier / Stockage", items: [{ label: "Sol industriel" }, { label: "Portes de quai" }, { label: "Système électrique" }, { label: "Ventilation" }] },
  { name: "Sanitaires", items: [{ label: "Murs / sol" }, { label: "Plomberie" }, { label: "Robinetterie" }] },
  { name: "Extérieur", items: [{ label: "Parking" }, { label: "Clôture" }, { label: "Éclairage extérieur" }] },
];
const STATES = [{ v: "neuf", l: "Neuf" }, { v: "bon", l: "Bon" }, { v: "moyen", l: "Moyen" }, { v: "mauvais", l: "Mauvais" }, { v: "hs", l: "HS" }];

export default function InventoriesAdmin() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [leases, setLeases] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [newForm, setNewForm] = useState({ lease_id: "", type: "entry", rooms: DEFAULT_ROOMS, notes: "" });

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/admin/inventories"), api.get("/admin/leases")]);
    setItems(a.data); setLeases(b.data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/admin/inventories", newForm);
      toast.success("État des lieux créé");
      setCreating(false); setNewForm({ lease_id: "", type: "entry", rooms: DEFAULT_ROOMS, notes: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec"); }
  };

  const remove = async (id) => {
    if (!confirm("Supprimer ?")) return;
    await api.delete(`/admin/inventories/${id}`);
    load();
  };

  const updateItem = (rIdx, iIdx, field, value) => {
    if (!editing) return;
    const rooms = JSON.parse(JSON.stringify(editing.rooms || []));
    rooms[rIdx].items[iIdx][field] = value;
    setEditing({ ...editing, rooms });
  };

  const saveEdit = async () => {
    try {
      await api.put(`/admin/inventories/${editing.id}`, { rooms: editing.rooms, notes: editing.notes });
      toast.success("Mis à jour"); setEditing(null); load();
    } catch { toast.error("Échec"); }
  };

  const sign = async (id) => {
    // Admin (landlord) signs — simple confirmation, signature stored as text marker
    try {
      const sig = "data:image/svg+xml;base64," + btoa(`<svg xmlns='http://www.w3.org/2000/svg' width='200' height='60'><text x='10' y='40' font-family='cursive' font-size='24' fill='#111'>Signé par admin · ${new Date().toLocaleDateString("fr-FR")}</text></svg>`);
      await api.put(`/admin/inventories/${id}`, { landlord_signature: sig });
      toast.success("Signé côté bailleur");
      load();
    } catch { toast.error("Échec"); }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-default)" }}>
      <Navbar /><Toaster richColors position="top-right" />
      <section className="max-w-6xl mx-auto px-6 md:px-8 py-12">
        <button onClick={() => navigate("/admin")} className="text-xs font-bold uppercase tracking-widest mb-6 flex items-center gap-2"><ArrowLeft className="w-3 h-3"/> Retour</button>
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">États des lieux</h1>
            <p className="text-sm opacity-70 mt-2">Entrée, sortie, signatures électroniques.</p>
          </div>
          <button onClick={() => setCreating(true)} data-testid="new-inventory-btn" className="btn-brutal btn-brutal-solar px-5 py-3 font-bold uppercase tracking-wider text-sm flex items-center gap-2"><Plus className="w-4 h-4"/> Nouvel état</button>
        </div>

        <div className="bg-[color:var(--bg-surface)] border overflow-x-auto" style={{ borderColor: "var(--border-default)" }}>
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-white"><tr><th className="px-3 py-3 text-left text-xs uppercase">Date</th><th className="px-3 py-3 text-left text-xs uppercase">Type</th><th className="px-3 py-3 text-left text-xs uppercase">Locataire</th><th className="px-3 py-3 text-left text-xs uppercase">Local</th><th className="px-3 py-3 text-left text-xs uppercase">Statut</th><th className="px-3 py-3 text-right text-xs uppercase">Actions</th></tr></thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan="6" className="p-12 text-center opacity-50">Aucun état.</td></tr>}
              {items.map(I => (
                <tr key={I.id} className="border-t border-slate-900/10" data-testid={`row-inv-${I.id}`}>
                  <td className="px-3 py-3 text-xs font-mono">{new Date(I.created_at).toLocaleDateString("fr-FR")}</td>
                  <td className="px-3 py-3"><span className="tag">{I.type === "entry" ? "Entrée" : "Sortie"}</span></td>
                  <td className="px-3 py-3 text-xs">{I.tenant?.name}</td>
                  <td className="px-3 py-3 text-xs">{I.property?.title}</td>
                  <td className="px-3 py-3 text-xs"><span className={`px-2 py-1 font-bold uppercase text-xs ${I.state === "signed" ? "bg-emerald-200" : "bg-slate-200"}`}>{I.state}</span>{I.tenant_signature && <div className="text-[10px] mt-1 text-emerald-700">✓ Locataire signé</div>}{I.landlord_signature && <div className="text-[10px] text-emerald-700">✓ Bailleur signé</div>}</td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    <button onClick={() => setEditing(I)} className="p-2 border border-slate-900 hover:bg-slate-900 hover:text-white mr-1"><Edit className="w-4 h-4"/></button>
                    {!I.landlord_signature && <button onClick={() => sign(I.id)} title="Signer (bailleur)" className="p-2 border border-slate-900 hover:bg-emerald-200 mr-1">✍</button>}
                    <button onClick={() => remove(I.id)} className="p-2 border border-red-300 text-red-600"><Trash2 className="w-4 h-4"/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {creating && (
          <Modal onClose={() => setCreating(false)} title="Nouvel état des lieux" testid="new-inv-modal">
            <div className="space-y-4 p-6">
              <F label="Bail">
                <select value={newForm.lease_id} onChange={e => setNewForm({...newForm, lease_id: e.target.value})} className="input">
                  <option value="">— Sélectionnez —</option>
                  {leases.map(L => <option key={L.id} value={L.id}>{L.tenant?.name} — {L.property?.title}</option>)}
                </select>
              </F>
              <F label="Type">
                <select value={newForm.type} onChange={e => setNewForm({...newForm, type: e.target.value})} className="input">
                  <option value="entry">Entrée</option><option value="exit">Sortie</option>
                </select>
              </F>
              <p className="text-xs opacity-70">La grille par défaut (4 pièces × 4 items) sera créée. Vous pourrez la modifier après création.</p>
            </div>
            <div className="p-6 border-t flex justify-end gap-3"><button onClick={() => setCreating(false)} className="btn-brutal bg-white border border-slate-900 px-4 py-2 text-sm font-bold uppercase">Annuler</button><button onClick={create} disabled={!newForm.lease_id} className="btn-brutal btn-brutal-solar px-5 py-2 text-sm font-bold uppercase disabled:opacity-50">Créer</button></div>
          </Modal>
        )}

        {editing && (
          <Modal onClose={() => setEditing(null)} title={`État des lieux — ${editing.type === "entry" ? "Entrée" : "Sortie"}`} testid="edit-inv-modal">
            <div className="space-y-6 p-6">
              {(editing.rooms || []).map((r, rIdx) => (
                <div key={rIdx} className="border border-slate-900/15 p-4">
                  <h3 className="font-display font-bold mb-3">{r.name}</h3>
                  <div className="space-y-2">
                    {r.items?.map((it, iIdx) => (
                      <div key={iIdx} className="grid grid-cols-[1fr_120px_2fr] gap-2 items-center text-sm">
                        <span>{it.label}</span>
                        <select value={it.state || ""} onChange={e => updateItem(rIdx, iIdx, "state", e.target.value)} className="input">
                          <option value="">—</option>
                          {STATES.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
                        </select>
                        <input value={it.comments || ""} onChange={e => updateItem(rIdx, iIdx, "comments", e.target.value)} placeholder="Commentaire..." className="input" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <F label="Notes">
                <textarea rows={3} value={editing.notes || ""} onChange={e => setEditing({...editing, notes: e.target.value})} className="input resize-none" />
              </F>
            </div>
            <div className="p-6 border-t flex justify-end gap-3"><button onClick={() => setEditing(null)} className="btn-brutal bg-white border border-slate-900 px-4 py-2 text-sm font-bold uppercase">Fermer</button><button onClick={saveEdit} className="btn-brutal btn-brutal-solar px-5 py-2 text-sm font-bold uppercase flex items-center gap-1"><Save className="w-4 h-4"/> Enregistrer</button></div>
          </Modal>
        )}
      </section>
      <Footer />
    </div>
  );
}

const Modal = ({ title, onClose, children, testid }) => (
  <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-slate-900" onClick={e => e.stopPropagation()} data-testid={testid}>
      <div className="flex items-center justify-between p-6 border-b border-slate-900/10"><h2 className="font-display font-black text-xl">{title}</h2><button onClick={onClose}><X className="w-5 h-5"/></button></div>
      {children}
    </div>
  </div>
);

const F = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2 block">{label}</span>
    {children}
  </label>
);
